// AI 텔레메트리 turn-fact 스키마 단일 소스 — 라이브 trace ≡ 테스트 facts //
// P0 (2026-06-11): nodeRunner.formatTurnResult + nodeAgent bufferSnapshot 빌더를 동작 0변경으로 추출.
// 소비처: nodeRunner.mjs(Phase 1 직렬화) / nodeAgent.mjs(턴 종료 snapshot) / 라이브 useAiChat(P2c 배선 예정).
// 설계: docs/analysis/2026-06/2026-06-W2/2026-06-11-ai-telemetry-design-adversarial-verification-analysis.md §7

// turn-fact 스키마 버전 — 필드 추가/변경 시 bump (라이브 trace ↔ facts 드리프트 추적) //
// v1.1 (P2c): llmCalls(lean)/backendCalls(lean)/extraDisplays 편입 — raw 대용량(rawRequest/rawResponse)은
//             turn-fact에 비포함, ai_response per-call 행(POST llmCalls[] DTO)으로만 전송.
// v1.2 (OB-1): guardFirings[](가드 발동 구조화 링버퍼 cap 50)/hadFalseNoDataRisk(거짓 no-data 리스크 플래그) 편입 — replay 판독 비용 절감.
export const SCHEMA_VERSION = '1.2';

// LLM 호출 lean 직렬화 — raw 대용량 제외, 토큰/식별/플래그만 (turn-fact용) //
export function leanLlmCalls(llmCalls) {
  if (!Array.isArray(llmCalls)) return [];

  return llmCalls.map(c => ({
    iteration: c.iteration,
    respId: c.respId ?? null,
    callType: c.callType ?? null,
    modelUsed: c.modelUsed ?? null,
    maxOutputTokens: c.maxOutputTokens ?? null,
    inputTokens: c.inputTokens ?? null,
    outputTokens: c.outputTokens ?? null,
    reasoningTokens: c.reasoningTokens ?? null,
    cachedTokens: c.cachedTokens ?? null,
    totalTokens: c.totalTokens ?? null,
    elapsedMs: c.elapsedMs ?? null,
    error: c.error ?? false,
    errorDetails: c.errorDetails ?? null,
    messageCount: c.messageCount ?? null,
    rawRequestDeduped: c.rawRequestDeduped ?? false,
    rawRequestTruncated: c.rawRequestTruncated ?? false,
    modelParams: c.modelParams ?? null,
    generatedContent: c.generatedContent ?? null,
  }));
}

// 백엔드 호출 lean 직렬화 — 원시응답 제외 (turn-fact용) //
export function leanBackendCalls(backendCalls) {
  if (!Array.isArray(backendCalls)) return [];

  return backendCalls.map(c => ({
    index: c.index,
    type: c.type ?? null,
    intention: c.intention ?? null,
    endpoint: c.endpoint ?? null,
    method: c.method ?? null,
    fetchMethod: c.fetchMethod ?? null,
    params: c.params ?? null,
    statusCode: c.statusCode ?? null,
    success: c.success ?? null,
    errorMessage: c.errorMessage ?? null,
    attemptStatuses: c.attemptStatuses ?? null,
    attemptCount: c.attemptCount ?? null,
    latencyMs: c.latencyMs ?? null,
    transformApplied: c.transformApplied ?? false,
    rawResponseTruncated: c.rawResponseTruncated ?? false,
  }));
}

// 턴 결과 → turn-fact 직렬화 (v1.0: nodeRunner.formatTurnResult 추출 / v1.1: 시임1·2 산출 편입) //
export function buildTurnFact(turn, agentResult) {
  return {
    input: turn.input,
    originalInput: turn.originalInput || turn.input,
    log: agentResult.log || [],
    finalText: agentResult.finalText || '',
    intention: agentResult.intention || null,
    displayResult: agentResult.displayResult ? {
      totalCount: agentResult.displayResult.totalCount,
      columns: agentResult.displayResult.columns,
      columnLabels: agentResult.displayResult.columnLabels,
      availableFields: agentResult.displayResult.availableFields,
      // 전수 보존 — 진단용으로 모든 row 필요
      items: agentResult.displayResult.items || [],
    } : null,
    // [v1.1] 다축 추가 표 — primary 외 축 (없으면 null)
    extraDisplays: agentResult.extraDisplays || null,
    // 전수 보존 — recipients 전체 + mode 전체
    smsRecommendation: agentResult.smsRecommendation || null,
    // Buffer snapshot — destroy 직전 캡처. slot별 itemCount/summary/availableFields/resolvedColumnLabels/intention
    bufferSnapshot: agentResult.bufferSnapshot || null,
    // [v1.2 OB-1] 가드 발동 전수 ({guard, hadLiveItems, tool, iteration}) + 거짓 no-data 리스크 플래그
    guardFirings: agentResult.guardFirings || null,
    hadFalseNoDataRisk: agentResult.hadFalseNoDataRisk ?? null,
    // [v1.1] 시임1 LLM 호출 전수 (lean — raw 제외) + 시임2 백엔드 호출 전수 (lean)
    llmCalls: leanLlmCalls(agentResult.llmCalls),
    backendCalls: leanBackendCalls(agentResult.backendCalls),
    schemaVersion: SCHEMA_VERSION,
    elapsedMs: agentResult.elapsedMs,
  };
}

// AgentSession 버퍼 → 진단용 스냅샷 (구 nodeAgent 인라인 빌더 — 동작 0변경) //
// 실패 시 null 반환 — 진단 데이터일 뿐 에이전트 동작에 영향 없어야 함 //
export function snapshotBuffers(session) {
  try {
    if (session && session.buffers instanceof Map) {
      const slots = {};

      for (const [slotName, buf] of session.buffers.entries()) {
        const _itemsLen = Array.isArray(buf?.items) ? buf.items.length : 0;

        slots[slotName] = {
          itemCount: _itemsLen,
          // [3단검증 보강] items raw 전수 (cap 20) — column drift / 옵션 누락 / 중복 ratio 판정
          items: Array.isArray(buf?.items) ? buf.items.slice(0, 20) : null,
          itemsTotalCount: _itemsLen,
          itemsCapped: _itemsLen > 20,
          summary: buf?.summary || null,
          availableFields: buf?.availableFields || null,
          resolvedColumnLabels: buf?.resolvedColumnLabels || null,
          intention: buf?.intention || null,
        };
      }

      return { activeSlot: session.activeSlot || null, slots };
    }
  }
  catch (_e) { /* 스냅샷 실패 무시 — null fallback */ }

  return null;
}
